import React, { useMemo } from 'react'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import {
  DemographicsSection,
  Divider,
  MetricCard,
  StatsCard,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { CARNEGIE_CLASSIFICATION_SIZE_OPTIONS } from 'utils/carnegieClassificationSize'
import { formatAddress, formatInteger } from 'utils/formatting'
import useDocumentTitle from 'components/useDocumentTitle'
import { useHideFooter } from 'context/layout'

export function NewUniversityPage({ universityID }: { universityID?: number }) {
  const api = apiService()
  const { data, isLoading } = api.useGetUniversity(universityID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(
    `${data?.university?.institution_name || 'Not Found'} (University) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.university?.institution_name
        ? [{ title: 'Company', label: data.university.institution_name }]
        : [],
    [data?.university?.institution_name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  // Calculate estimated meals per day (dormitory capacity * 3 meals)
  const estimatedMealsPerDay = data?.university?.dormitory_capacity
    ? data.university.dormitory_capacity * 3
    : null

  // Build stats for the summary tab
  const universityStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Dormitory Capacity',
      value: data?.university?.dormitory_capacity
        ? formatInteger(data.university.dormitory_capacity)
        : 'N/A',
    },
    {
      label: 'Total Students Enrolled',
      value: data?.university?.total_students_enrolled
        ? formatInteger(data.university.total_students_enrolled)
        : 'N/A',
    },
    {
      label: 'Meal Plan Provided',
      value: data?.university?.meal_plan_provided ? 'Yes ✅' : 'No ⛔',
    },
    {
      label: 'Food Management Co.',
      value: data?.university?.food_management_company || 'N/A',
    },
    {
      label: 'Parent Food Mgmt. Co.',
      value: data?.university?.food_management_company_parent || 'N/A',
    },
    {
      label: 'Estimated Meals per Day',
      value: data?.university?.estimated_meals_per_day || 'N/A',
    },
  ]

  // Get Carnegie Classification label
  const carnegieClassificationLabel =
    CARNEGIE_CLASSIFICATION_SIZE_OPTIONS.find(
      (option) =>
        option.value ===
        data?.university?.carnegie_classification_2021_size_setting
    )?.label ?? 'N/A'

  // Student Stats Section
  const studentStats = [
    {
      label: 'Student Composition',
      value:
        data?.university
          ?.carnegie_classification_2021_size_setting_student_composition ||
        'N/A',
    },
    {
      label: 'Carnegie Classification: Year',
      value:
        data?.university?.carnegie_classification_2021_size_setting_year ||
        'N/A',
    },
    {
      label: 'Carnegie Classification: Size',
      value: carnegieClassificationLabel,
    },
    {
      label: 'Carnegie Size Setting',
      value:
        data?.university?.carnegie_classification_2021_size_setting_size ||
        'N/A',
    },
    {
      label: 'HBCU Status',
      value: data?.university?.hbcu_status === 1 ? 'Yes' : 'No',
    },
    {
      label: 'Parent Food Mgmt. Co.',
      value: data?.university?.food_management_company_parent || 'N/A',
    },
    {
      label: 'Estimated Meals per Day',
      value: estimatedMealsPerDay ? formatInteger(estimatedMealsPerDay) : 'N/A',
    },
    {
      label: 'Campus Count',
      value: data?.university?.campus_count
        ? formatInteger(data.university.campus_count)
        : 'N/A',
    },
  ]

  const summaryTabContent: React.ReactNode[] = []

  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      address={
        formatAddress({
          street: data?.university?.address,
          city: data?.university?.city,
          state: data?.university?.state_abbr,
          zip: data?.university?.zipcode,
        }) ?? undefined
      }
      phone={data?.phone_number || undefined}
      googlePlaceUrl={data?.university?.google_place_url}
    />
  )

  if (data?.doors_count && data?.doors_count > 0) {
    summaryTabContent.push(
      <MetricCard
        key="doors"
        value={data?.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
    )
  }

  summaryTabContent.push(
    <StatsCard key="university-stats" stats={universityStats} />,
    <Divider key="divider-1" />,
    <StatsCard key="student-stats" stats={studentStats} />
  )

  // Campus Location - similar to HQ City for chains
  const campusLocation = data?.university?.address
    ? `${data.university.city}, ${data.university.state_abbr} - USA`
    : 'N/A'

  // Campus Demographics/Info (similar to location summaries for chains but single location)
  const campusInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: data?.university?.address || 'N/A',
    },
    {
      label: 'ZIP Code',
      value: data?.university?.zipcode || 'N/A',
    },
    {
      label: 'State',
      value: data?.university?.state_abbr || 'N/A',
    },
    {
      label: 'Tuition & Fees (2021-22)',
      value: data?.university?.tuition_fees_2021_22
        ? `$${formatInteger(data.university.tuition_fees_2021_22)}`
        : 'N/A',
    },
  ]

  // Build location details tab content (following chain pattern)
  const locationDetailsTabContent: React.ReactNode[] = [
    <MetricCard
      key="campus-location"
      value={campusLocation}
      label="Campus Location"
      variant="full-width"
    />,
    <StatsCard key="campus-info" stats={campusInfoStats} />,
  ]

  if (data?.university?.address_info) {
    locationDetailsTabContent.push(
      <Divider key="divider-2" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={data?.university?.address_info}
      />
    )
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot="Education - University"
        breadcrumbs={[
          {
            title: 'University',
            breadcrumb: data!.university!.institution_name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        sidebar={{
          header: {
            name: data!.university!.institution_name!,
            domain: data!.university!.website_address!,
            logoUrl: undefined, // Universities don't have logos in the data
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={universityID}
          companyName={data!.university!.institution_name!}
          websiteAddress={data!.university!.website_address!}
          companyTypeSlug="education-cu"
          tableTypeSlug="education-cu"
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {data && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.university?.institution_name || '',
            domain: data.university?.website_address,
          }}
          subCategory={{
            name: 'University',
            slug: 'education-cu',
            description: 'University',
          }}
          companyName={data.university?.institution_name}
        />
      )}
    </>
  )
}
